/** @prettier */

import React from 'react';
import {useState, useEffect, useContext, memo} from 'react';
import {
  Grid,
  Box,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {isUndefined} from 'lodash';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import {GraphButton, ControlButton} from './SiteSubContentsIconButton';
import {ContextApplyFilterTab} from './MySiteArea';
//interface import
import * as apiIF from '../../Type/api';
//type import
import {layoutUnitListType, graphProps, mysite} from '../../Type/TypeDef';
//Style import
import '../../css/common/common.css';

import * as mySiteLib from './mySiteLibrary';
import {useUnitStates} from '../../hooks/states/useUnitStates';
import {useUnitPow} from '../../hooks/states/useUnitPow';
import {
  invertComma,
  numberWithPeriod,
  roundOff,
} from '../../functions/convertString';
import {sortByUnicode} from '../../functions/sort';
import {iconBlack, iconWhite, iconYellow, iconRed} from '../../define';

//空調機一覧の幅一覧
//変更した場合、ホバーした際のボタンのレイアウトを確認すること
const gridXsAirUnit = 5.5;
const gridXsAirState = 2.5;
const gridXsAirKwh = 4;

/**
 * マイサイト操作画面（空調機）
 * @param props props
 * @returns JSX
 */
const ViewUnit = memo(
  (props: {
    siteProfile: apiIF.resParameters.siteInformations;
    floorFilter: string;
    groupFilter: string;
    selectedItem: string;
    unitTypeList: layoutUnitListType;
    setSelectedItem: React.Dispatch<React.SetStateAction<string>>;
    setGraphParam: graphProps['setGraphParam'];
    setOperationParam: (
      target: apiIF.reqParameters.postCommandsSitesQuery['mode'] | undefined,
      targetID: string,
      siteID: string
    ) => void;
  }) => {
    const trans = useTranslation().t; //翻訳関数

    //prettier-ignore
    //空調機消費電力量
    const [viewUnitPwData, setViewUnitPwData] = useState<mysite.unitPwData[]>([]);
    //prettier-ignore
    //空調機表示用リスト この配列の順番に画面描画される
    const [groupFloorList, setGroupFloorList] = useState<mysite.unitGroupList[]>([]);
    /** サイトID */
    const siteId = props.siteProfile['site-id'];

    //空調機単位の状態値を画面読み込み時と、1分ごとに取得するフック
    const {unitsData, groupData} = useUnitStates(siteId);
    //空調機単位の消費電力を画面読み込み時と、1分ごとに取得するフック
    const {unitPwData} = useUnitPow(siteId, props.siteProfile['timezone']);

    //ユニットデータ表示用リストの作成
    useEffect(() => {
      setGroupFloorList(sortUnitList(groupData));
    }, [groupData]);

    //運転モード毎の消費電力をまとめる
    useEffect(() => {
      setViewUnitPwData(calcUnitPwData(unitPwData));
    }, [unitPwData]);

    /**
     * フロア名変換テーブル
     * @param floor フロア
     * @returns フロア名
     */
    const convertFloorName = (floor: string) => {
      switch (floor) {
        case '14':
        case '13': //fall through
        case '12': //fall through
        case '11': //fall through
        case '10': //fall through
        case '9': //fall through
        case '8': //fall through
        case '7': //fall through
        case '6': //fall through
        case '5': //fall through
        case '4': //fall through
        case '3': //fall through
        case '2': //fall through
        case '1': //fall through
        case '0': //fall through
          return `floor${floor}`;
        case '-1':
          return 'basement1';
        case '-2':
          return 'basement2';
        case '-3':
          return 'basement3';
        case '-4':
          return 'basement4';
        default:
          return '';
      }
    };

    return (
      <>
        {/** フィルタ情報 */}
        <Box className="subContents__content__filterChip">
          <FilterButton
            floorFilter={props.floorFilter}
            groupFilter={props.groupFilter}
            groupList={groupFloorList}
          />
        </Box>
        {/** ヘッダ */}
        <Grid
          container
          alignItems="center"
          className="subContents__content__header">
          <Grid
            item
            xs={gridXsAirUnit}
            className="subContents__content__header__category">
            {trans('ViewUnit.header.groupUnit')}
          </Grid>
          <Grid
            item
            xs={gridXsAirState}
            className="subContents__content__header__status">
            {trans('ViewUnit.header.status')}
          </Grid>
          <Grid
            item
            xs={gridXsAirKwh}
            className="subContents__content__header__power">
            {trans('ViewUnit.header.consumption')}
          </Grid>
        </Grid>
        {/** グループごとに空調機を出力 */}
        <Box className="subContents__content__body">
          <Stack>
            {groupFloorList
              //指定したグループのみでフィルタする
              .filter(groupInfo => {
                return (
                  (props.groupFilter === '' ||
                    groupInfo.groupID === props.groupFilter) &&
                  groupInfo.unitIds.length > 0
                );
              })
              .map((groupInfo, index) => {
                return (
                  <Grid
                    container
                    key={`UnitViewData${groupInfo.groupID}}`}
                    className={
                      index === 0 ? '' : 'subContents__content__body--basic'
                    }>
                    <Grid
                      item
                      xs={12}
                      className="subContents__content__body__data__group">
                      {
                        //prettier-ignore
                        `${trans(`ViewUnit.label.floor.${convertFloorName(groupInfo.floor)}`)}, ${groupInfo.groupName}`
                      }
                    </Grid>
                    {groupInfo.unitIds.map(unitID => {
                      /** ユニットデータ */
                      const unitData = unitsData.find(info => {
                        return info['unit-id'] === unitID;
                      });
                      /** 消費電力量データ */
                      const unitPwData = viewUnitPwData.find(info => {
                        return info['unit-id'] === unitID;
                      });
                      /** 対象機器のIDと型式情報 */
                      const targetIUInfo = props.unitTypeList.find(
                        info => info.unitID === unitID
                      );
                      if (
                        isUndefined(unitData) ||
                        isUndefined(unitPwData) ||
                        isUndefined(targetIUInfo)
                      ) {
                        return <Grid item xs={12} key={unitID}></Grid>;
                      }
                      /** 対象室内機の型式 */
                      const unitType = targetIUInfo.unitType;

                      return (
                        <Grid item xs={12} key={unitID}>
                          <UnitView
                            siteID={siteId}
                            groupID={groupInfo.groupID}
                            isCommand={groupInfo.groupAuthority === 'manager'}
                            unitData={unitData}
                            unitType={unitType}
                            unitPowerData={unitPwData}
                            selectedUnit={props.selectedItem}
                            setSelectedUnit={props.setSelectedItem}
                            setGraphParam={props.setGraphParam}
                            setOperationParam={props.setOperationParam}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                );
              })}
          </Stack>
        </Box>
      </>
    );
  }
);

/**
 * グループの情報を並び替え
 * @param groupData グループの情報
 * @returns 並び替えた結果
 */
const sortUnitList = (
  groupData: apiIF.resParameters.groupInformations[]
): mysite.unitGroupList[] => {
  /** ユニット受信データ */
  //ソートで配列が破壊されるので配列をコピー
  let localGroupData = groupData.concat();

  //フロア階層とグループ名でソート
  localGroupData.sort((a, b) => {
    //先にフロア階数で降順ソート
    if (a.floor === undefined || b.floor === undefined) {
      return 0;
    }
    if (a.floor !== b.floor) {
      return Number(b.floor) - Number(a.floor);
    }
    //フロア階層ソート後にグループ名で昇順ソート
    if (a['group-name'] === undefined || b['group-name'] === undefined) {
      return 0;
    }
    if (a['group-name'] !== b['group-name']) {
      return a['group-name'] < b['group-name'] ? -1 : 1;
    }
    return 0;
  });
  localGroupData.forEach(info => {
    if (info['unit-informations'] !== undefined) {
      info['unit-informations'].sort(sortByUnicode('unit-name'));
    }
  });

  /** グループに属するユニットリスト */
  const localUnitGroupList = localGroupData.map(groupInfo => {
    /** ユニットが所属するフロア名とグループ名を組み合わせた名前 */
    const floorGroup = `${groupInfo.floor}${groupInfo['group-name']}`;

    return {
      floorGroup: floorGroup,
      groupID: groupInfo['group-id'],
      groupName:
        groupInfo['group-name'] === undefined ? '' : groupInfo['group-name'],
      groupAuthority:
        groupInfo['group-authority'] === undefined
          ? 'none'
          : groupInfo['group-authority'],
      floor: groupInfo.floor === undefined ? '' : groupInfo.floor,
      unitIds:
        groupInfo['unit-informations'] === undefined
          ? []
          : groupInfo['unit-informations'].map(info => info['unit-id']),
    };
  });

  return localUnitGroupList;
};

/**
 * ユニット別の消費電力量を算出
 * @param unitData 状態値
 * @returns 算出結果
 */
const calcUnitPwData = (
  unitData: apiIF.resParameters.slStates[]
): mysite.unitPwData[] => {
  /**
   * API戻り値の消費電力量WをKW変換(小数点以下四捨五入)
   */
  const convNumKw = mySiteLib.convNumKw;

  return unitData.map(unitData => {
    //受信データに消費電力量がない場合は、0とする
    if (isUndefined(unitData['power-consumption'])) {
      return {'unit-id': unitData['unit-id'], 'power-consumption': '0'};
    }

    //消費電力量の、冷房・暖房・その他を合計する
    const cooling = convNumKw(unitData['power-consumption']['cooling-mode']);
    const heating = convNumKw(unitData['power-consumption']['heating-mode']);
    const other = convNumKw(unitData['power-consumption']['other-mode']);
    const pwData = String(cooling + heating + other);
    return {'unit-id': unitData['unit-id'], 'power-consumption': pwData};
  });
};

/**
 * フィルタの適用情報の表示 押下でフィルタキャンセル
 * @param props props
 * - floorFilter 適用しているフロア
 * - groupFilter 適用しているグループ
 * - groupList グループの情報
 * @return JSX
 */
const FilterButton = memo(
  (props: {
    floorFilter: string;
    groupFilter: string;
    groupList: mysite.unitGroupList[];
  }) => {
    const trans = useTranslation().t; //翻訳関数
    /** フィルタ、タブ状態をセットする関数 */
    const {applyFilter} = useContext(ContextApplyFilterTab);

    if (props.floorFilter === '' || props.groupFilter === '') return <></>;

    /** 表示するグループの情報 */
    const displayGroupInfo = props.groupList.find(info => {
      return info.groupID === props.groupFilter;
    });
    if (displayGroupInfo === undefined) return <></>;
    //prettier-ignore
    /** 表示するラベル名 */
    const label = `${trans(`ViewUnit.buttonTxt.filter.floor.floor${props.floorFilter.replace('-', 'B')}`)}, ${displayGroupInfo.groupName}`;

    return (
      // フィルタの解除ボタン
      <Chip
        deleteIcon={
          <img
            src={iconRed.CLOSE.src}
            alt={`${trans(
              `ViewUnit.body.image.alternativeText.${iconRed.CLOSE.alt}`
            )}`}
            className="subContents__content__filterChip__icon"
          />
        }
        label={
          <Box title={label} className="subContents__content__filterChip__text">
            {label}
          </Box>
        }
        onDelete={() => {
          applyFilter({floor: '', groupID: '', otherUnitType: ''});
        }}
        variant="outlined"
        id="mv-unitsClearFilterBtn"
      />
    );
  }
);

/**
 * グループ内の空調機一覧
 * @param props props
 * - siteID サイトID
 * - groupID グループID
 * - isCommand ユーザが空調機に指令ができるかどうか
 * - unitData 空調機の状態値
 * - unitType 空調機の型式
 * - unitPowerData 空調機の消費電力量
 * - selectedUnit 選択状態の空調機
 * - setSelectedUnit 選択状態の空調機のset関数
 * - setGraphParam グラフのパラメータのset関数
 * - setOperationParam 指令のパラメータのset関数
 */
const UnitView = memo(
  (props: {
    siteID: string;
    groupID: string;
    isCommand: boolean;
    unitData: apiIF.resParameters.slStates;
    unitType: layoutUnitListType[0]['unitType'];
    unitPowerData: mysite.unitPwData;
    selectedUnit: string;
    setSelectedUnit: React.Dispatch<React.SetStateAction<string>>;
    setGraphParam: graphProps['setGraphParam'];
    setOperationParam: (
      target: apiIF.reqParameters.postCommandsSitesQuery['mode'] | undefined,
      targetID: string,
      siteID: string
    ) => void;
  }) => {
    const navigate = useNavigate();
    const trans = useTranslation().t; //翻訳関数
    /** フィルタ、タブ状態をセットする関数 */
    const {applyFilter} = useContext(ContextApplyFilterTab);

    //空調機がホバー状態かどうか
    const [hoveringUnit, setHoveringUnit] = useState('');
    //prettier-ignore
    const [hoveringIcon, setHoveringIcon] = useState<'' | 'graph' | 'control'>(''); //ホバー状態のアイコン

    /** 選択されているかどうか */
    const isSelected = props.unitData['unit-id'] === props.selectedUnit;
    /** ホバーされているかどうか */
    const isHover = props.unitData['unit-id'] === hoveringUnit;
    /** エラーユニットフラグ エラー有:true エラー無:false */
    const errUnitFlg =
      props.unitData['error-code'] !== undefined &&
      props.unitData['error-code'] !== '' && //無課金状態
      props.unitData['error-code'] !== '00';

    /**
     * 運転時の空調機アイコンのデザイン決定
     * @param unitType 空調機の型式
     * @returns 画像とalt属性用の翻訳キー
     */
    const runIconStyle = (unitType: layoutUnitListType[0]['unitType']) => {
      switch (unitType) {
        case 'AC':
        default:
          return {
            src: iconWhite.AC_ON.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconWhite.AC_ON.alt}`,
          };
        case 'FDT':
          return {
            src: iconWhite.FDT_ON.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconWhite.FDT_ON.alt}`,
          };
        case 'FDTS':
          return {
            src: iconWhite.FDTS_ON.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconWhite.FDTS_ON.alt}`,
          };
        case 'FDTW':
          return {
            src: iconWhite.FDTW_ON.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconWhite.FDTW_ON.alt}`,
          };
        case 'FDE':
          return {
            src: iconWhite.FDE_ON.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconWhite.FDE_ON.alt}`,
          };
        case 'FDF':
          return {
            src: iconWhite.FDF_ON.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconWhite.FDF_ON.alt}`,
          };
        case 'FDFW':
          return {
            src: iconWhite.FDFW_ON.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconWhite.FDFW_ON.alt}`,
          };
        case 'FDK':
          return {
            src: iconWhite.FDK_ON.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconWhite.FDK_ON.alt}`,
          };
        case 'FDU':
          return {
            src: iconWhite.FDU_ON.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconWhite.FDU_ON.alt}`,
          };
        case 'VENT':
          return {
            src: iconWhite.VENTILATOR_UNIT_ON.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconWhite.VENTILATOR_UNIT_ON.alt}`,
          };
        case 'LIGHT':
          return {
            src: iconWhite.LIGHT_UNIT_ON.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconWhite.LIGHT_UNIT_ON.alt}`,
          };
      }
    };
    /**
     * 停止時のアイコンのデザイン決定
     * @param unitType 空調機の型式
     * @returns 画像とalt属性用の翻訳キー
     */
    const stopIconStyle = (unitType: layoutUnitListType[0]['unitType']) => {
      switch (unitType) {
        case 'AC':
        default:
          return {
            src: iconBlack.AC_OFF.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconBlack.AC_OFF.alt}`,
          };
        case 'FDT':
          return {
            src: iconBlack.FDT_OFF.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconBlack.FDT_OFF.alt}`,
          };
        case 'FDTS':
          return {
            src: iconBlack.FDTS_OFF.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconBlack.FDTS_OFF.alt}`,
          };
        case 'FDTW':
          return {
            src: iconBlack.FDTW_OFF.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconBlack.FDTW_OFF.alt}`,
          };
        case 'FDE':
          return {
            src: iconBlack.FDE_OFF.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconBlack.FDE_OFF.alt}`,
          };
        case 'FDF':
          return {
            src: iconBlack.FDF_OFF.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconBlack.FDF_OFF.alt}`,
          };
        case 'FDFW':
          return {
            src: iconBlack.FDFW_OFF.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconBlack.FDFW_OFF.alt}`,
          };
        case 'FDK':
          return {
            src: iconBlack.FDK_OFF.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconBlack.FDK_OFF.alt}`,
          };
        case 'FDU':
          return {
            src: iconBlack.FDU_OFF.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconBlack.FDU_OFF.alt}`,
          };
        case 'VENT':
          return {
            src: iconBlack.VENTILATOR_UNIT_OFF.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconBlack.VENTILATOR_UNIT_OFF.alt}`,
          };
        case 'LIGHT':
          return {
            src: iconBlack.LIGHT_UNIT_OFF.src,
            transKeyForAlt: `ViewUnit.body.image.alternativeText.${iconBlack.LIGHT_UNIT_OFF.alt}`,
          };
      }
    };
    /**
     * 運転/停止アイコンのデザインの決定
     * @param runStop 運転/停止
     * @param unitType 空調機の型式
     * @returns デザイン
     */
    const getRunStopIconStyle = (
      runStop: apiIF.resParameters.slStates['run-stop'],
      unitType: layoutUnitListType[0]['unitType']
    ) => {
      switch (runStop) {
        case 'run':
          const {src: runSrc, transKeyForAlt: runTransKeyForAlt} =
            runIconStyle(unitType);
          return {
            src: runSrc,
            alt: trans(runTransKeyForAlt),
            class: 'subContents__content__body__data__status__runStopIcon--run',
          };
        case 'stop':
          const {src: stopSrc, transKeyForAlt: stopTransKeyForAlt} =
            stopIconStyle(unitType);
          return {
            src: stopSrc,
            alt: trans(stopTransKeyForAlt),
            class:
              'subContents__content__body__data__status__runStopIcon--stop',
          };
        default:
          return {
            src: iconBlack.MISSING.src,
            alt: trans(
              `ViewUnit.body.image.alternativeText.${iconBlack.MISSING.alt}`
            ),
            class:
              'subContents__content__body__data__status__runStopIcon--unitTypeIcon',
          };
      }
    };
    /**
     * 運転モードアイコンのデザインの決定
     * @param modeState 運転モード
     * @returns デザイン
     */
    const getModeIconStyle = (
      modeState: apiIF.resParameters.slStates['operation-mode']
    ) => {
      switch (modeState) {
        case 'auto':
          return {
            src: iconWhite.AUTO.src,
            alt: trans(
              `ViewUnit.body.image.alternativeText.${iconWhite.AUTO.alt}`
            ),
            class:
              'subContents__content__body__data__status__operationModeIcon--auto',
          };
        case 'dehumidify':
          return {
            src: iconWhite.DRYING.src,
            alt: trans(
              `ViewUnit.body.image.alternativeText.${iconWhite.DRYING.alt}`
            ),
            class:
              'subContents__content__body__data__status__operationModeIcon--dry',
          };
        case 'cooling':
          return {
            src: iconWhite.COOLING.src,
            alt: trans(
              `ViewUnit.body.image.alternativeText.${iconWhite.COOLING.alt}`
            ),
            class:
              'subContents__content__body__data__status__operationModeIcon--cooling',
          };
        case 'fan':
          return {
            src: iconWhite.BLOWER.src,
            alt: trans(
              `ViewUnit.body.image.alternativeText.${iconWhite.BLOWER.alt}`
            ),
            class:
              'subContents__content__body__data__status__operationModeIcon--fan',
          };
        case 'heating':
          return {
            src: iconWhite.HEATING.src,
            alt: trans(
              `ViewUnit.body.image.alternativeText.${iconWhite.HEATING.alt}`
            ),
            class:
              'subContents__content__body__data__status__operationModeIcon--heating',
          };
        default:
          return {
            src: iconBlack.MISSING.src,
            alt: trans(
              `ViewUnit.body.image.alternativeText.${iconBlack.MISSING.alt}`
            ),
            class:
              'subContents__content__body__data__status__operationModeIcon--non',
          };
      }
    };
    /** 運転/停止アイコンのデザイン */
    const {
      src: runStopIconSrc,
      alt: runStopIconAlt,
      class: runStopIconClassName,
    } = getRunStopIconStyle(props.unitData['run-stop'], props.unitType);
    /** 運転モードアイコンのデザイン */
    const {
      src: modeIconSrc,
      alt: modeIconAlt,
      class: modeIconClassName,
    } = getModeIconStyle(props.unitData['operation-mode']);
    /** 運転モード表示名変換 */
    const operationModeName = (
      modeState: apiIF.resParameters.slStates['operation-mode']
    ) => {
      switch (modeState) {
        case 'auto':
          return trans('ViewUnit.body.parameter.value.mode.auto');
        case 'dehumidify':
          return trans('ViewUnit.body.parameter.value.mode.dry');
        case 'cooling':
          return trans('ViewUnit.body.parameter.value.mode.cooling');
        case 'fan':
          return trans('ViewUnit.body.parameter.value.mode.fan');
        case 'heating':
          return trans('ViewUnit.body.parameter.value.mode.heating');
        default:
          return '--';
      }
    };

    /**
     * アコーディオンの状態変更時
     * @param event
     * @param isExpanded
     */
    const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded) {
        props.setSelectedUnit(props.unitData['unit-id']);
        navigate(
          `/pages/my-site/site-detail/${props.siteID}/state/group/${props.groupID}`
        );
      } else {
        props.setSelectedUnit('');
      }
    };

    return (
      <Accordion
        disableGutters={true}
        expanded={isSelected}
        onChange={handleChange}
        onMouseEnter={() => setHoveringUnit(props.unitData['unit-id'])}
        onMouseLeave={() => setHoveringUnit('')}
        className={
          isSelected
            ? 'subContents__content__body__data--selected'
            : 'subContents__content__body__data'
        }>
        {/* アコーディオン閉じ状態表示 */}
        <AccordionSummary
          aria-controls={`${props.unitData['unit-name']}-content`}
          className="subContents__content__body__data__accordionSummary">
          <Grid container alignItems="center">
            {/** 空調機名 */}
            <Grid
              item
              xs={gridXsAirUnit}
              title={props.unitData['unit-name']}
              onClick={() => {
                if (props.unitData.floor !== undefined) {
                  applyFilter({
                    floor: props.unitData.floor,
                    groupID: props.groupID,
                    otherUnitType: '',
                  });
                }
              }}
              className="subContents__content__body__data__category">
              {props.unitData['unit-name']}
            </Grid>
            {/** 状態値アイコン、異常表示 */}
            <Grid
              item
              xs={gridXsAirState}
              container
              columnSpacing={1}
              alignItems="center">
              {/** 運転/停止アイコン */}
              <Grid item xs="auto">
                <Box className={runStopIconClassName}>
                  <img
                    src={runStopIconSrc}
                    alt={runStopIconAlt}
                    className="subContents__content__body__data__status__runStopIcon__image"
                  />
                </Box>
              </Grid>
              {/** 運転モードアイコン */}
              <Grid item xs="auto">
                <Box className={modeIconClassName}>
                  <img
                    src={modeIconSrc}
                    alt={modeIconAlt}
                    className="subContents__content__body__data__status__operationModeIcon__image"
                  />
                </Box>
              </Grid>
              {/** 異常表示 */}
              {/** 異常時のみ表示する */}
              {errUnitFlg && (
                <Grid item xs="auto">
                  <img
                    src={
                      isHover && hoveringIcon === ''
                        ? iconWhite.ERROR.src //空調機をホバー かつ グラフ・機器操作アイコンをホバー
                        : iconYellow.ERROR.src
                    }
                    alt={`${trans(
                      'ViewUnit.body.image.alternativeText.errIcon'
                    )}`}
                    className="subContents__content__body__data__status__errorIconForUnit"
                  />
                </Grid>
              )}
            </Grid>
            {/** 消費電力量、グラフ、機器操作 */}
            <Grid
              item
              xs={gridXsAirKwh}
              container
              justifyContent="flex-end"
              alignItems="center"
              className="subContents__content__body__data__powerGraphOpe">
              {/** 消費電力量 */}
              <Grid
                item
                xs="auto"
                className="subContents__content__body__data__powerGraphOpe__power">
                {numberWithPeriod(
                  roundOff(props.unitPowerData['power-consumption'])
                )}
              </Grid>
              <Grid
                item
                xs={3}
                className="subContents__content__body__data__powerGraphOpe__KWh">
                {trans('ViewUnit.body.unit')}
              </Grid>
              {/** グラフ、機器操作 */}
              <Grid
                item
                xs="auto"
                className="subContents__content__body__data__powerGraphOpe__graphOpeIcon--accordion">
                {/* グラフアイコン */}
                <GraphButton
                  isHover={isHover && hoveringIcon === 'graph'}
                  setHoveringIcon={setHoveringIcon}
                  isIconDisabled={props.unitData['error-code'] === ''} //エラーコードが空文字の場合、無課金GWのユニットなので無効化
                  onClick={() => {
                    props.setGraphParam(
                      'sl',
                      props.unitData['unit-id'],
                      'bar',
                      props.unitData['unit-name'],
                      undefined, //tabNameは熱源機用のため未使用
                      undefined, //childUnitInfoはQton用のため未使用
                      props.groupID
                    );
                  }}
                />
                {/* 機器操作アイコン */}
                {props.isCommand && (
                  <ControlButton
                    isHover={isHover && hoveringIcon === 'control'}
                    setHoveringIcon={setHoveringIcon}
                    isIconDisabled={props.unitData['error-code'] === ''} //エラーコードが空文字の場合、無課金GWのユニットなので無効化
                    onClick={() => {
                      props.setOperationParam(
                        'sl',
                        props.unitData['unit-id'],
                        props.siteID
                      );
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </AccordionSummary>
        {/* アコーディオン開き状態表示 */}
        <AccordionDetails className="subContents__content__body__data__accordionDetails">
          {/* 全体 */}
          <Grid
            container
            className="subContents__content__body__data__accordionDetails">
            {/* 状態値 */}
            <Grid item xs={9.5} container alignItems="flex-end">
              {/* 室内温度値 */}
              <Grid item xs={4}>
                <AccordionDetailsState
                  title={trans('ViewUnit.body.parameter.title.roomTemp')}
                  value={`${trans('ViewUnit.body.parameter.value.unit.temp', {
                    value:
                      props.unitData['indoor-temperature'] !== undefined &&
                      props.unitData['indoor-temperature'] !== ''
                        ? invertComma(
                            roundOff(props.unitData['indoor-temperature'], 1)
                          )
                        : '--',
                  })}`}
                />
              </Grid>
              {/** 設定温度値 */}
              <Grid item xs={4}>
                <AccordionDetailsState
                  title={trans('ViewUnit.body.parameter.title.setTemp')}
                  value={`${trans('ViewUnit.body.parameter.value.unit.temp', {
                    value:
                      props.unitData['preset-temperature'] !== undefined &&
                      props.unitData['preset-temperature'] !== ''
                        ? invertComma(
                            roundOff(props.unitData['preset-temperature'], 1)
                          )
                        : '--',
                  })}`}
                />
              </Grid>
              {/** 運転モード値 */}
              <Grid item xs={4}>
                <AccordionDetailsState
                  title={trans('ViewUnit.body.parameter.title.mode')}
                  value={operationModeName(props.unitData['operation-mode'])}
                />
              </Grid>
            </Grid>
            {/** グラフ、機器操作 */}
            <Grid item xs={2.5}>
              {/* グラフアイコン */}
              <GraphButton
                isHover={isHover && hoveringIcon === 'graph'}
                setHoveringIcon={setHoveringIcon}
                isIconDisabled={props.unitData['error-code'] === ''} //エラーコードが空文字の場合、無課金GWのユニットなので無効化
                onClick={() => {
                  props.setGraphParam(
                    'sl',
                    props.unitData['unit-id'],
                    'bar',
                    props.unitData['unit-name'],
                    undefined, //tabNameは熱源機用のため未使用
                    undefined, //childUnitInfoはQton用のため未使用
                    props.groupID
                  );
                }}
              />
              {/* 機器操作アイコン */}
              {props.isCommand && (
                <ControlButton
                  isHover={isHover && hoveringIcon === 'control'}
                  setHoveringIcon={setHoveringIcon}
                  isIconDisabled={props.unitData['error-code'] === ''} //エラーコードが空文字の場合、無課金GWのユニットなので無効化
                  onClick={() => {
                    props.setOperationParam(
                      'sl',
                      props.unitData['unit-id'],
                      props.siteID
                    );
                  }}
                />
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  }
);

/**
 * アコーディオン開き状態で表示する状態値
 * @param props props
 * - title タイトル
 * - value 状態値
 * @returns JSX
 */
const AccordionDetailsState = (props: {
  title: string;
  value: string | React.ReactElement;
}) => {
  return (
    <Stack>
      <Box className="subContents__content__body__data__accordionDetails__title">
        {props.title}
      </Box>
      <Box className="subContents__content__body__data__accordionDetails__value">
        {props.value}
      </Box>
    </Stack>
  );
};

export default ViewUnit;
